import { StandardLoader } from '../StandardLoader'
import { PasswordInput } from '../PasswordInput'
import { useTar } from '@/modules'

interface EmailSignInFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>
  isLoading: boolean
  isDisabled: boolean
  onForgotPassword?: () => void
  onSignUp?: () => void
}

const hideSignup = true

export const EmailSignInForm = ({
  onSubmit,
  isLoading,
  isDisabled,
  onForgotPassword,
  onSignUp
}: EmailSignInFormProps) => {
  const { t } = useTar()
  return (
    <>
      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-card text-muted">{t('auth.loginWithEmail')}</span>
        </div>
      </div>

      {/* Email Sign-In Form */}
      <form onSubmit={onSubmit} className="space-y-4" autoComplete="on">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            {t('auth.emailAddress')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="username"
            required
            disabled={isDisabled}
            className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground
                     placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                     focus:border-accent transition-colors
                     ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            placeholder={t('auth.emailPlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
            {t('common.password')}
          </label>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            required
            disabled={isDisabled}
            placeholder={t('auth.passwordPlaceholder')}
          />
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              disabled={isDisabled}
              name="rememberMe"
              className="h-4 w-4 text-accent focus:ring-ring border-border rounded"
            />
            <span className="ml-2 text-sm text-muted-foreground">{t('common.rememberMe')}</span>
          </label>
          {onForgotPassword && (
            <button
              type="button"
              disabled={isDisabled}
              onClick={onForgotPassword}
              className={`text-sm transition-colors
                ${isDisabled
                  ? 'text-muted cursor-not-allowed'
                  : 'text-accent hover:text-foreground'
                }`}
            >
              {t('common.forgotPassword')}
            </button>
          )}
        </div>

        <button
          type="submit"
          disabled={isDisabled}
          className={`w-full px-4 py-2 rounded-lg font-medium
                   focus:outline-none focus:ring-2 focus:ring-ring 
                   transition-colors duration-200 flex items-center justify-center
                   ${isDisabled
              ? 'bg-accent/50 text-white cursor-not-allowed opacity-75'
              : 'bg-accent text-white hover:bg-accent/90'
            }`}
        >
          {isLoading ? (
            <>
              <StandardLoader size="sm" inline />
              <span>{t('common.loading')}</span>
            </>
          ) : (
            t('common.login')
          )}
        </button>
      </form>

      {/* Footer */}
      {onSignUp && !hideSignup && (
        <div className="text-center mt-4">
          <p className="text-sm text-muted-foreground">
            {t('common.newToAppName')}{' '}
            <button
              onClick={onSignUp}
              className={`font-medium transition-colors
                ${isDisabled
                  ? 'text-muted cursor-not-allowed'
                  : 'text-accent hover:text-foreground'
                }`}
              disabled={isDisabled}
            >
              {t('common.joinAppName')}
            </button>
          </p>
        </div>
      )}
    </>
  )
} 